import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  FormHelperText,
  Paper,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

// Import Material Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import GavelIcon from '@mui/icons-material/Gavel';

/**
 * ModernCategorizedFieldsDemo - A demonstration component showing a modern approach to displaying
 * categorized form fields with Material UI
 * 
 * Design principles implemented:
 * 1. Card-based sections for clear visual separation
 * 2. Consistent spacing and layout
 * 3. Icons for improved visual recognition
 * 4. Responsive design with stacked fields on mobile, side-by-side on desktop
 * 5. Progressive disclosure through expandable sections
 */
const ModernCategorizedFieldsDemo = ({ fields, categories, onFieldChange, expandedByDefault = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState({});

  // Teal color for all category headers
  const headerColor = '#009688'; // Teal color that matches the application theme
  
  // Sample data for demonstration
  // In a real implementation, this would come from props
  const sampleCategories = {
    PERSONAL_INFO: {
      label: 'Personal Information',
      icon: <PersonIcon />,
      color: headerColor,
    },
    IDENTIFICATION: {
      label: 'Identification Documents',
      icon: <DescriptionIcon />,
      color: headerColor,
    },
    CONTACT_INFO: {
      label: 'Contact Information',
      icon: <ContactMailIcon />,
      color: headerColor,
    },
    ADDRESS: {
      label: 'Address Details',
      icon: <LocationOnIcon />,
      color: headerColor,
    },
    EMPLOYMENT: {
      label: 'Employment Information',
      icon: <WorkIcon />,
      color: headerColor,
    },
    FINANCIAL: {
      label: 'Financial Details',
      icon: <AccountBalanceIcon />,
      color: headerColor,
    },
    EDUCATION: {
      label: 'Education',
      icon: <SchoolIcon />,
      color: headerColor,
    },
    MEDICAL: {
      label: 'Medical Information',
      icon: <LocalHospitalIcon />,
      color: headerColor,
    },
    LEGAL: {
      label: 'Legal Information',
      icon: <GavelIcon />,
      color: headerColor,
    },
    SERVICE_SPECIFIC: {
      label: 'Service Information',
      icon: <ExpandMoreIcon />,
      color: headerColor,
    },
  };

  // Sample fields for demonstration
  const sampleFields = {
    PERSONAL_INFO: [
      { id: 'firstName', label: 'First Name', required: true, type: 'text' },
      { id: 'lastName', label: 'Last Name', required: true, type: 'text' },
      { id: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
      { id: 'nationality', label: 'Nationality', type: 'text' },
      { id: 'dateOfBirth', label: 'Date of Birth', type: 'date' }
    ],
    IDENTIFICATION: [
      { id: 'passportNumber', label: 'Passport Number', required: true, type: 'text' },
      { id: 'passportIssue', label: 'Issue Date', type: 'date' },
      { id: 'passportExpiry', label: 'Expiry Date', type: 'date' },
      { id: 'nationalId', label: 'National ID', type: 'text' },
    ],
    CONTACT_INFO: [
      { id: 'email', label: 'Email', required: true, type: 'email' },
      { id: 'phoneNumber', label: 'Phone Number', required: true, type: 'tel' },
    ],
    ADDRESS: [
      { id: 'address', label: 'Address', required: true, type: 'text' },
      { id: 'city', label: 'City', required: true, type: 'text' },
      { id: 'state', label: 'State/Province', type: 'text' },
      { id: 'postalCode', label: 'Postal/ZIP Code', type: 'text' },
    ]
  };

  // Handler for expanding/collapsing sections
  const handleAccordionChange = (categoryCode) => (event, isExpanded) => {
    setExpanded({ ...expanded, [categoryCode]: isExpanded });
  };

  // Mock handler for field changes
  const handleFieldChange = (field, value) => {
    console.log(`Field ${field} changed to:`, value);
    // In a real implementation, call onFieldChange prop
  };

  // The categories and fields to display
  const displayCategories = categories || sampleCategories;
  const displayFields = fields || sampleFields;

  return (
    <Box sx={{ mb: 4, mx: { xs: 1, md: 2 } }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Request Form
        </Typography>
        <Typography variant="body1">
          Please fill out the following information to proceed with your request.
          Required fields are marked with an asterisk (*).
        </Typography>
      </Paper>

      {/* Desktop View - Card Based Layout */}
      {!isMobile && (
        <Grid container spacing={3}>
          {Object.keys(displayCategories).map((categoryCode) => {
            const category = displayCategories[categoryCode];
            const categoryFields = displayFields[categoryCode];

            if (!categoryFields || categoryFields.length === 0) return null;

            return (
              <Grid item xs={12} key={categoryCode}>
                <Card elevation={2}>
                  <CardHeader
                    avatar={category.icon}
                    title={category.label}
                    sx={{
                      backgroundColor: category.color,
                      color: '#fff',
                      '& .MuiCardHeader-avatar': {
                        color: '#fff',
                      }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      {categoryFields.map((field) => (
                        <Grid item xs={12} sm={6} key={field.id}>
                          {field.type === 'select' ? (
                            <TextField
                              select
                              fullWidth
                              label={field.label}
                              required={field.required}
                              onChange={(e) => handleFieldChange(field.id, e.target.value)}
                              variant="outlined"
                              margin="normal"
                            >
                              {field.options?.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              fullWidth
                              label={field.label}
                              type={field.type}
                              required={field.required}
                              onChange={(e) => handleFieldChange(field.id, e.target.value)}
                              variant="outlined"
                              margin="normal"
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Mobile View - Accordion Based Layout for Progressive Disclosure */}
      {isMobile && (
        <Box>
          {Object.keys(displayCategories).map((categoryCode) => {
            const category = displayCategories[categoryCode];
            const categoryFields = displayFields[categoryCode];

            if (!categoryFields || categoryFields.length === 0) return null;

            const isExpanded = expanded[categoryCode] !== undefined ? expanded[categoryCode] : expandedByDefault;

            return (
              <Accordion
                key={categoryCode}
                expanded={isExpanded}
                onChange={handleAccordionChange(categoryCode)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: category.color, color: '#fff' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {category.icon}
                    <Typography sx={{ ml: 1 }} variant="subtitle1">{category.label}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {categoryFields.map((field) => (
                      <Grid item xs={12} key={field.id}>
                        {field.type === 'select' ? (
                          <TextField
                            select
                            fullWidth
                            label={field.label}
                            required={field.required}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            variant="outlined"
                            margin="normal"
                          >
                            {field.options?.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            label={field.label}
                            type={field.type}
                            required={field.required}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary">
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default ModernCategorizedFieldsDemo;
